import { BsArrowLeftCircleFill } from 'react-icons/bs';
import { ConfirmationResult, getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { firebaseConfig, generalBlue } from 'src/app-config';
import { hideLoadingModalReducer, showLoadingModalReducer } from 'src/store';
import { initializeApp } from 'firebase/app';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode, useGetIsMobile, useGetRecaptchaToken } from 'src/custom-hooks';
import { userServices } from 'src/services';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

type propsType = {
    loginUsingToken: () => Promise<void>;
    openAlertModalHandler: (title: string, message: string, animation: number, execution?: Function) => void;
    usePassword: () => void;
};

export const PhoneAuthentication: FC<propsType> = ({ loginUsingToken, openAlertModalHandler, usePassword }) => {
    const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
    const [otp, setOtp] = useState('');
    const [phone, setPhone] = useState('+54911');
    const dispatch = useDispatch();
    const getRecaptchaToken = useGetRecaptchaToken();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();
    const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

    const isValidSendPhoneNumber: boolean = useMemo(() =>
        phone.length > 9
    , [phone]);

    const isValidVerifyCode: boolean = useMemo(() =>
        !!confirmationResult && otp.length > 3
    , [confirmationResult, otp]);

    const sendPhoneNumber = async () => {
        if (!isValidSendPhoneNumber || !recaptchaVerifierRef.current) {
            return;
        }
        let result: ConfirmationResult | null = null;
        dispatch(showLoadingModalReducer());
        try {
            result = await signInWithPhoneNumber(auth, phone, recaptchaVerifierRef.current);
        } catch (error) {
            console.error('Error enviando código:', error);
        } finally {
            dispatch(hideLoadingModalReducer());
        }
        if (!result) {
            openAlertModalHandler('Algo falló', 'No se pudo enviar el SMS', 2);
            return;
        }
        setConfirmationResult(result);
        openAlertModalHandler('Número enviado', 'Cargar en el segundo campo la contraseña que llegue al celular', 1);
    }

    const verifyCode = async () => {
        if (!isValidVerifyCode || !confirmationResult) return;
        let firebaseToken: string | null = null;
        dispatch(showLoadingModalReducer());
        try {
            const firebaseUserCredential = await confirmationResult.confirm(otp);
            firebaseToken = await firebaseUserCredential.user.getIdToken();
        } catch (error) {
            console.error('Error verificando código:', error);
        } finally {
            dispatch(hideLoadingModalReducer());
        }
        if (!firebaseToken) {
            openAlertModalHandler('Algo falló', 'El servicio externo de contraseñas por SMS parece no funcionar', 2);
            return;
        }
        const recaptchaToken = await getRecaptchaToken();
        if (!recaptchaToken) {
            openAlertModalHandler("Problemas (6)", "Refrescar la página", 2);
            return;
        }
        dispatch(showLoadingModalReducer());
        const response = await userServices.LoginByPhone(firebaseToken, recaptchaToken);
        dispatch(hideLoadingModalReducer());
        if (!response?.success) {
            openAlertModalHandler('Algo falló', 'El código no es correcto o el teléfono no está registrado en la aplicación; hablar con un administrador', 2);
            return;
        }
        loginUsingToken();
    }

    useEffect(() => {
        if (!recaptchaVerifierRef.current) {
            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
                size: 'invisible',
            });
        }
    }, []);

    return (
        <div className={`border rounded ${isDarkMode ? 'bg-lightdark text-white' : 'bg-lightgrey'} py-3 px-2 ${isMobile ? '' : 'mt-5'}`}>
            <div id={'recaptcha-container'}></div>
            <h3 className={'text-center'}>
                Recibir una contraseña de un único uso al celular
            </h3>
            <div className={'container d-flex justify-content-center text-center mt-4 mb-2'}>
                <input type='text'
                    className={'form-control'}
                    style={{ width: '250px' }}
                    placeholder={'N° de tel. celular'}
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    onKeyUp={e => e.key === 'Enter' ? sendPhoneNumber() : null}
                />
                <button className={'btn btn-general-blue ms-1'}
                    style={{ width: '165px' }}
                    onClick={sendPhoneNumber}
                    disabled={!isValidSendPhoneNumber}
                >
                    Enviar Código
                </button>
            </div>
            <div className={'container d-flex justify-content-center text-center my-1'}>
                <input type='number'
                    className={'form-control'}
                    style={{ width: '250px' }}
                    placeholder='Código'
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    onKeyUp={e => e.key === 'Enter' ? verifyCode() : null}
                />
                <button className={'btn btn-general-blue ms-1'}
                    style={{ width: '165px' }}
                    onClick={verifyCode}
                    disabled={!isValidVerifyCode}
                >
                    Verificar Código
                </button>
            </div>
            <div className={'px-2 mt-4'}>
                <p className={'mb-1'} style={{ fontSize: '.8rem' }}>
                    *El número debe estar previamente registrado por un administrador
                </p>
                <p className={'mb-1'} style={{ fontSize: '.8rem' }}>
                    **El número debe estar en formato internacional (comúnmente +54911 y los siguientes 8 números)
                </p>
                <p className={'mb-3'} style={{ fontSize: '.8rem' }}>
                    ***El código tiene una hora de tolerancia
                </p>
            </div>
            <div className={'px-3'}>
                <p className={`${isDarkMode ? 'bg-white' : ''} rounded text-center py-2 mt-3`}
                    style={{
                        color: generalBlue,
                        fontSize: '1rem'
                    }}
                >
                    <BsArrowLeftCircleFill />
                    <span className={`ms-2 pointer`}
                        onClick={usePassword}
                    >
                        Cancelar y volver
                    </span>
                </p>
            </div>
        </div>
    );
}
